import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MapPin, Phone, Mail, Clock, MessageCircle } from "lucide-react";
import { STORE, CATEGORIES, API, saveSubscriber, waLink } from "../lib/store";
import { useEdit } from "../lib/content";
import { E } from "./edit/EditKit";
import { trackSubscribe, trackContactClick } from "../lib/analytics";
import { CreditNotice } from "./CreditNotice";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { toast } from "sonner";

export function WhatsAppFloat() {
  return (
    <a
      href={waLink("Hello Regal Jewels! I have a question about your jewelry.")}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat on WhatsApp"
      onClick={() => trackContactClick("whatsapp_float")}
      className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-[#25D366] hover:bg-[#1fb457] text-white rounded-full shadow-xl px-4 py-3.5 transition-colors duration-300 hover:scale-105"
      style={{ transition: "transform .25s ease, background-color .25s ease" }}
    >
      <MessageCircle size={22} strokeWidth={2} />
      <span className="hidden sm:inline text-sm font-medium pr-1">WhatsApp Us</span>
    </a>
  );
}

export default function Footer() {
  const [email, setEmail] = useState("");
  const { get } = useEdit();
  const [navPages, setNavPages] = useState([]);

  useEffect(() => {
    axios.get(`${API}/pages`).then((r) => setNavPages((r.data || []).filter((p) => p.inNav))).catch(() => {});
  }, []);

  const cats = (get("nav.categories", CATEGORIES.map((c) => ({ key: c.key, label: c.label }))) || []).filter((c) => !c.hidden);
  const phone = get("store.phone", STORE.phone);
  const whatsapp = get("store.whatsapp", STORE.whatsapp);
  const emailAddr = get("store.email", STORE.email);
  const address = get("store.address", STORE.address);
  const hoursText = get("store.hoursText", STORE.hours.map((h) => `${h.d}: ${h.h}`).join("\n"));

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return toast.error("Please enter a valid email");
    try {
      await saveSubscriber(email);
      trackSubscribe();
      setEmail("");
      toast.success("Thank you for joining our mailing list!");
    } catch (err) {
      toast.error("Could not subscribe. Please check your email and try again.");
    }
  };

  return (
    <footer className="bg-white border-t border-[#ece5d5] mt-24">
      {/* Newsletter */}
      <div className="bg-[#faf7f0] border-b border-[#ece5d5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h3 className="font-heading text-3xl text-[#1a1713]">Join Our Mailing List</h3>
            <p className="text-sm mt-2 text-[#7a7160]">New arrivals &amp; exclusive offers.</p>
          </div>
          <form onSubmit={subscribe} className="flex w-full md:w-auto gap-0">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              className="rounded-none bg-white border-[#d8cdb5] text-[#1a1713] placeholder:text-[#9c9482] w-full md:w-80 h-12 focus-visible:ring-[#a8842c]"
            />
            <Button type="submit" className="rounded-none bg-[#1a1713] hover:bg-[#3a2f1d] text-[#f3ead2] font-medium tracking-[0.15em] uppercase text-xs h-12 px-7 transition-colors duration-300">
              Notify Me
            </Button>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <img src="/regal-logo-full.png" alt="Regal Jewels logo" className="h-28 w-auto mb-4" />
          <p className="font-heading text-xl text-[#1a1713] tracking-[0.14em]">REGAL JEWELS</p>
          <E as="p" id="footer.blurb" multiline default="18k, 22k & 24k gold, silver & diamond jewelry. Visit our Chicago store or shop with USA-wide delivery."
            className="text-sm mt-3 leading-relaxed text-[#7a7160]" />
        </div>

        <div>
          <h4 className="footer-head">Collections</h4>
          <ul className="space-y-2.5">
            {cats.map((c) => (
              <li key={c.key}>
                <Link to={`/collections/${c.key}`} className="footer-link">{c.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="footer-head">Customer Care</h4>
          <ul className="space-y-2.5">
            <li><Link to="/shop" className="footer-link">Shop All Jewelry</Link></li>
            <li><Link to="/book" className="footer-link">Book an Appointment</Link></li>
            <li><Link to="/contact" className="footer-link">Contact & Inquiries</Link></li>
            <li><Link to="/gold-rate-chicago" className="footer-link">Today's Gold Rate</Link></li>
            <li><Link to="/education" className="footer-link">Jewelry Education</Link></li>
            <li><Link to="/stories" className="footer-link">Regal Stories</Link></li>
            {navPages.map((p) => (
              <li key={p.slug}><Link to={`/p/${p.slug}`} className="footer-link">{p.title}</Link></li>
            ))}
            <li>
              <a onClick={() => trackContactClick("whatsapp_footer")} href={waLink("Hello Regal Jewels!")} target="_blank" rel="noopener noreferrer" className="footer-link">WhatsApp Chat</a>
            </li>
            <li>
              <a href="https://www.google.com/maps/search/?api=1&query=Regal+Jewels+Inc+2625+W+Devon+Ave+Chicago+IL+60659" target="_blank" rel="noopener noreferrer" className="footer-link">Find Us on Google</a>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="footer-head">Visit Our Store</h4>
          <ul className="space-y-3 text-sm text-[#5c5240]">
            <li className="flex gap-3"><MapPin size={17} strokeWidth={1.5} className="text-[#a8842c] shrink-0 mt-0.5" /><E id="store.address" default={STORE.address} /></li>
            <li className="flex gap-3"><Phone size={17} strokeWidth={1.5} className="text-[#a8842c] shrink-0 mt-0.5" />
              <span>
                <a href={`tel:${phone}`} onClick={() => trackContactClick("phone")} className="hover:text-[#a8842c] transition-colors"><E id="store.phone" default={STORE.phone} /></a>
                <br /><span className="text-[#8f8571]">WhatsApp: <E id="store.whatsapp" default={STORE.whatsapp} /></span>
              </span>
            </li>
            <li className="flex gap-3"><Mail size={17} strokeWidth={1.5} className="text-[#a8842c] shrink-0 mt-0.5" />
              <a href={`mailto:${emailAddr}`} onClick={() => trackContactClick("email")} className="hover:text-[#a8842c] transition-colors break-all"><E id="store.email" default={STORE.email} /></a>
            </li>
            <li className="flex gap-3"><Clock size={17} strokeWidth={1.5} className="text-[#a8842c] shrink-0 mt-0.5" />
              <E id="store.hoursText" multiline default={STORE.hours.map((h) => `${h.d}: ${h.h}`).join("\n")} />
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-[#ece5d5] py-6 text-center space-y-2.5">
        <CreditNotice placement="footer" variant="footer" />
        <p className="text-xs tracking-[0.15em] uppercase text-[#9c9482]">
          © {new Date().getFullYear()} Regal Jewels, Chicago. All rights reserved.
        </p>
      </div>
    </footer>
  );
}
